import { useState } from "react";

import isNil from "lodash/isNil";
import type { LayoutChangeEvent } from "react-native";
import { useWindowDimensions } from "react-native";

import {
  GreetingCardHeight,
  MessageWidthBreakpoint,
  MessageWidthPercentage,
} from "@/constants/UI";
import { FAIconName, IconButton, Image, Text } from "@/ui/core";
import { Box, VStack } from "@/ui/layout";
import { HeroImageBackground } from "@/ui/layout/HeroImageBackground";
import { ColorToken, LayoutToken } from "@/ui/theme/tokens";
import {
  getGreetingPaddingProps,
  getGreetingProps,
} from "@/utils/greeting.util";
import { isWeb } from "@/utils/platform.util";
import { joinTestIDs } from "@/utils/string.util";

interface GreetingCardProps {
  message: string;
  onHelpPress: () => void;
  userName?: string;
}

const baseTestID = "greeting-card";

// on web, the message width is responsive to window size (no fixed-height measurement needed)
const getMessageMaxWidth = (windowWidth: number): string => {
  if (windowWidth > MessageWidthBreakpoint.Large) {
    return MessageWidthPercentage.Large;
  }
  if (windowWidth > MessageWidthBreakpoint.Medium) {
    return MessageWidthPercentage.Medium;
  }
  return MessageWidthPercentage.Small;
};

const GreetingCard = ({
  message,
  onHelpPress,
  userName,
}: GreetingCardProps) => {
  const { width: windowWidth } = useWindowDimensions();

  // on native, the card height is measured on first render (via onLayout) and then held fixed,
  // so that the height doesn't jump once userName data finishes loading
  const [fixedHeight, setFixedHeight] = useState<number | undefined>(
    isWeb() ? GreetingCardHeight : undefined,
  );
  const onLayout = (event: LayoutChangeEvent) => {
    if (isWeb() || !isNil(fixedHeight)) {
      return;
    }

    // if the username is empty, we still want the card to always have the same fixed height
    // so that if the username is empty due to data still loading,
    // the card height doesn't jump once the data is finally loaded
    const { layout } = event.nativeEvent;
    const { height: expandedHeight } = layout;
    setFixedHeight(expandedHeight);
  };

  const messageMaxWidth = isWeb() ? getMessageMaxWidth(windowWidth) : "70%";

  const { greetingText } = getGreetingProps({ fixedHeight, userName });
  const { paddingBottom, paddingTop, shouldJustifyContent } =
    getGreetingPaddingProps({ fixedHeight, userName });

  return (
    <HeroImageBackground
      height={GreetingCardHeight}
      imageSource={
        isWeb()
          ? require("@/assets/images/hero-background-web.png")
          : require("@/assets/images/hero-background.png")
      }
    >
      <Box
        height={fixedHeight}
        justifyContent={shouldJustifyContent ? "center" : undefined}
      >
        <VStack
          gap={8}
          onLayout={onLayout}
          paddingBottom={paddingBottom}
          paddingHorizontal={LayoutToken.ContentPaddingHorizontal}
          paddingTop={paddingTop}
          testID={joinTestIDs(baseTestID, "variable-layout")}
        >
          <Image
            size={24}
            testID={joinTestIDs(baseTestID, "logo")}
            uriSource={require("@/assets/images/equip-logo.png")}
          />
          <Text
            color={ColorToken.Primary90Variant}
            testID={joinTestIDs(baseTestID, "user-name")}
            variant="h3"
          >
            {greetingText}
          </Text>
          <Box maxWidth={messageMaxWidth}>
            <Text
              color={ColorToken.Primary90Variant}
              testID={joinTestIDs(baseTestID, "message")}
              variant="body"
            >
              {message}
            </Text>
          </Box>
        </VStack>
        {!isWeb() && (
          <Box position="absolute" right={12} top={12}>
            <IconButton
              iconName={FAIconName.HeadsetSolid}
              onPress={onHelpPress}
              testID={joinTestIDs(baseTestID, "headset")}
              variant="primarySolid"
            />
          </Box>
        )}
      </Box>
    </HeroImageBackground>
  );
};

export type { GreetingCardProps };
export { GreetingCard };
